import { AxiosResponse } from 'axios'
import get from 'lodash/get'
import { IAdminChangePasswordDTO, IChangePasswordDTO } from 'types/auth'
import { IFilter, PaginationList, Where } from 'types/common'
import { api } from 'API'
import { IGroupMember } from 'interfaces/group'
import { IProcessFilter, IProcessWithRelations } from 'interfaces/process'
import {
  IAuditWithRelations,
  ICreateEditUserRequest,
  IEditEditUserRequest,
  IUpdateUserPermission,
  IUser,
  IUserWithRelations
} from 'interfaces/user'
import { BackendSuccessResponse } from 'interfaces/common'
import { IAthleteProfile } from 'API/profile'

export async function createUser(user: ICreateEditUserRequest): Promise<IUser> {
  const createdUser: AxiosResponse = await api.post('/users', user)
  return createdUser.data
}

export async function getUsers(filter: IFilter<IUser> = {}): Promise<IUser[]> {
  const user: AxiosResponse = await api.get('/users', { params: { filter } })
  return user.data
}

// ─── Collaborator candidates (Edit Collaborators picker) ──────────────────────
// Active player + coach members of a single team (the draft's team). `orgId`
// scopes the query (also overrides the interceptor's active-org auto-stamp).

export interface ICollaboratorCandidate {
  id: number
  name: string
  email?: string
  image?: string
}

export async function getCollaboratorCandidates(params: {
  orgId: number
  search?: string
  limit?: number
  offset?: number
}): Promise<{ results: ICollaboratorCandidate[]; totalCount: number }> {
  const response: AxiosResponse = await api.get('/collaborator-candidates', { params })
  return response.data
}

// ─── User-based enrollment candidates (used by PlansPage AssignModal) ─────────

export interface IEnrollmentCandidateUser {
  id: number
  name: string
  email?: string
  image?: string
  isEnrolled: boolean
}

export interface IEnrollmentCandidateGroup {
  id: number
  name: string
  users: IEnrollmentCandidateUser[]
}

export async function getEnrollmentCandidates(params: {
  search?: string
  excludeAssignmentId?: number
}): Promise<{ groups: IEnrollmentCandidateGroup[] }> {
  const response: AxiosResponse = await api.get('/enrollment-candidates/users', { params })
  return response.data
}

// ─── Athlete membership candidates (used by TrainingAssignment + AssignModal) ──

export interface IEnrollmentCandidateAthlete {
  membershipId: number
  name: string
  avatarUrl?: string
  isEnrolled: boolean
}

export interface IAthleteEnrollmentCandidateGroup {
  id: number
  name: string
  athletes: IEnrollmentCandidateAthlete[]
}

/** Fetch athlete profile candidates for the training-assignment enroll modal. */
export async function getAthleteEnrollmentCandidates(params: {
  search?: string
  excludeAssignmentId?: number
  /** Narrows candidates to the active rosters of these seasonal Teams. */
  teamIds?: number[]
}): Promise<{ groups: IAthleteEnrollmentCandidateGroup[] }> {
  const response: AxiosResponse = await api.get('/enrollment-candidates/athlete-profiles', { params })
  return response.data
}

/** Fetch all org athlete profiles (no enrollment exclusion) for filter dropdowns. */
export async function getOrgAthleteProfiles(search?: string): Promise<{ groups: IAthleteEnrollmentCandidateGroup[] }> {
  const params: Record<string, string> = {}
  if (search) params.search = search
  const response: AxiosResponse = await api.get('/enrollment-candidates/athlete-profiles', { params })
  return response.data
}

// ─── Coach membership candidates (used by AssignModal coach tab + FilterModals) ──

export interface IEnrollmentCandidateCoach {
  membershipId: number
  name: string
  avatarUrl?: string
}

/** Flat list — coaches are not grouped; identity key is membershipId. */
export interface ICoachEnrollmentCandidateResponse {
  coaches: IEnrollmentCandidateCoach[]
}

/** Fetch coach profile candidates for the practice assignment modal. */
export async function getCoachEnrollmentCandidates(params: {
  search?: string
  /** Narrows candidates to the coaches assigned to these seasonal Teams. */
  teamIds?: number[]
}): Promise<ICoachEnrollmentCandidateResponse> {
  const response: AxiosResponse = await api.get('/enrollment-candidates/coach-profiles', { params })
  return response.data
}

export type UserListSortBy =
  | 'lastUpdated'
  | 'lastCreated'
  | 'roleAsc'
  | 'roleDesc'
  | 'nameAsc'
  | 'nameDesc'
  | 'lastLogin'

export interface IUserListParams {
  pageSize: number
  pageIndex: number
  sortBy?: UserListSortBy
  noPagination?: boolean
  userTypes?: number[]
  groupIds?: number[]
  /**
   * Cross-org scope. Empty/omitted = full cross-org aggregate (BE returns rows
   * from every membership the user has). Non-empty = scope to the listed teams.
   * Serialized CSV on the wire (`?orgIds=1,2,3`).
   */
  orgIds?: number[]
  keyword?: string
  /**
   * Drop members already on this Team. Used by the add-to-roster picker so it only
   * offers people who are not on the team yet — a soft-removed player counts as
   * off the team and is offered again.
   */
  excludeTeamId?: number
  // Advanced member filters (membership-PK scoped). Serialized CSV on the wire;
  // backend support is added incrementally — unknown params are ignored server-side.
  statuses?: string[]
  permissions?: number[]
  activities?: string[]
  lastLogin?: string
}

export async function getUserList(params: IUserListParams): Promise<PaginationList<IUserWithRelations>> {
  const queryParams: Record<string, string | number | boolean> = {
    pageSize: params.pageSize,
    pageIndex: params.pageIndex
  }
  if (params.sortBy) queryParams.sortBy = params.sortBy
  if (params.noPagination) queryParams.noPagination = true
  if (params.userTypes && params.userTypes.length > 0) queryParams.userTypes = params.userTypes.join(',')
  if (params.groupIds && params.groupIds.length > 0) queryParams.groups = params.groupIds.join(',')
  if (params.orgIds && params.orgIds.length > 0) queryParams.orgIds = params.orgIds.join(',')
  if (params.keyword) queryParams.keyword = params.keyword
  if (params.statuses && params.statuses.length > 0) queryParams.statuses = params.statuses.join(',')
  if (params.permissions && params.permissions.length > 0) queryParams.permissions = params.permissions.join(',')
  if (params.activities && params.activities.length > 0) queryParams.activities = params.activities.join(',')
  if (params.lastLogin) queryParams.lastLogin = params.lastLogin
  if (params.excludeTeamId !== undefined) queryParams.excludeTeamId = params.excludeTeamId

  const user: AxiosResponse = await api.get('/users/list', { params: queryParams })
  return user.data
}

export async function countUsers(where: Where<IUser> = {}): Promise<number> {
  const user: AxiosResponse = await api.get('/users/count', { params: { where } })
  return get(user, 'data.count', 0)
}

export async function countUsersAuth(where: Where<IUser> = {}): Promise<number> {
  const user: AxiosResponse = await api.get('/auth/count', { params: { where } })
  return get(user, 'data.count', 0)
}

export async function getUserById(userId: number, filter: IFilter<IUser> = {}): Promise<IUserWithRelations> {
  const user: AxiosResponse = await api.get(`/users/${userId}?filter=${JSON.stringify(filter)}`)
  return user.data
}

export async function getUserDetailById(userId: number): Promise<IUserWithRelations> {
  const response: AxiosResponse = await api.get(`/users/${userId}/detail`)
  return response.data
}

export async function updateUserById(userId: number, userData: IEditEditUserRequest = {}): Promise<void> {
  await api.patch(`/users/${userId}`, userData)
}

export async function updateUserAuthMethod(
  userId: number,
  data: { authMethodOverride?: 'password' | 'sso' | null; forcePasswordChangeOnNextLogin?: boolean }
): Promise<void> {
  await api.patch(`/admin/users/${userId}/auth-method`, data)
}

export async function addFcmToken(userId: number, fcmToken: string): Promise<void> {
  await api.post(`/users/${userId}/fcm-tokens`, { fcmToken })
}

export async function validatePassword(userId: number, password: string): Promise<boolean> {
  const result = await api.post(`/users/${userId}/validate-password`, { password })
  return result.data
}

export async function changePassword(data: IChangePasswordDTO): Promise<boolean> {
  const result = await api.post('/users/change-password', data)
  return result.data
}

export async function adminChangePassword(data: IAdminChangePasswordDTO): Promise<boolean> {
  const result = await api.post('/admin/users/change-password', data)
  return result.data
}

export async function getRecentlyViewed(limit: number = 10): Promise<IAuditWithRelations[]> {
  const response: AxiosResponse = await api.get(`/users/me/recently-viewed?limit=${limit}`)
  return response.data
}

export async function getUserPermissionsOnCollections(
  userId: number,
  collectionIds: number[]
): Promise<{ userPermissions: IGroupMember[]; id: number }[]> {
  const response: AxiosResponse = await api.post(`/users/${userId}/collections/permissions`, { collectionIds })
  return response.data
}

export async function shareCollections(userId: number, collectionIds: number[]): Promise<void> {
  await api.post(`/users/${userId}/share-collections`, collectionIds)
}

export async function unshareCollection(userId: number, collectionId: number): Promise<void> {
  await api.post(`/users/${userId}/unshare-collection`, collectionId)
}

export async function updateUserPermissionById(userId: number, data: IUpdateUserPermission): Promise<void> {
  await api.patch(`/users/${userId}/permission`, data)
}

export async function canUserCreateCollections(userId: number): Promise<boolean> {
  const response: AxiosResponse = await api.get(`/users/${userId}/can-create-collections`)
  return response.data
}

// Admin/owner removes another member — org-scoped (the interceptor stamps
// ?orgId from the active org, which the BE uses for the admin-role check).
export async function removeMembership(membershipId: number): Promise<void> {
  await api.delete(`/memberships/${membershipId}`)
}

// Self-service "leave team" from the cross-org Teams page. Account-scoped
// (/users/me/* → no ?orgId stamp, no org-context) so it works even when no org
// is active; the BE enforces self-removal. Distinct from removeMembership above,
// which stays org-scoped for the admin remove-member action.
export async function leaveMyTeam(membershipId: number): Promise<void> {
  await api.delete(`/users/me/memberships/${membershipId}`)
}

// Change a member's org role (e.g. Coach <-> Admin). Backend restricts this to Owner/Admin callers.
export async function updateMembershipRole(membershipId: number, roleId: number): Promise<void> {
  await api.patch(`/memberships/${membershipId}`, { roleId })
}

export interface IPlayerClassChange {
  /** A Class slug for the Organization's type; null clears it. */
  playerClass: string | null
  /** Kept only when `playerClass` is `other`. */
  playerClassCustomLabel?: string | null
}

/** Sets or clears a Player's Class in the active Organization (Owner/Admin). */
export async function updateMembershipPlayerClass(membershipId: number, change: IPlayerClassChange): Promise<void> {
  await api.patch(`/memberships/${membershipId}`, change)
}

/**
 * One Player's membership as the User Detail page shows it: the athlete-owned
 * profile, their Class in this Organization, and the Teams and Player Groups they
 * are on. Read from GET /memberships/{id}/player-detail.
 */
export interface IPlayerMemberDetail {
  membershipId: number
  /** Controlling account — the recipient for Send message. */
  userId?: number
  organizationId: number
  organizationName?: string
  organizationType?: string
  roleId: number
  status?: string
  playerClass?: string | null
  playerClassCustomLabel?: string | null
  athleteProfile: IAthleteProfile
  teams: Array<{ id: number; name: string; jerseyNumber?: string }>
  playerGroups: Array<{ id: number; name: string }>
}

export async function getPlayerMemberDetail(membershipId: number): Promise<IPlayerMemberDetail> {
  const response: AxiosResponse = await api.get(`/memberships/${membershipId}/player-detail`)
  return response.data
}

// Email verification APIs
export async function bindWorkEmail(workEmail: string): Promise<BackendSuccessResponse> {
  const response = await api.post('/email/bind-email', { ssoEmail: workEmail })
  return response.data
}

export async function verifyWorkEmail(token: string): Promise<BackendSuccessResponse> {
  const response = await api.post('/email/verify-email', { token })
  return response.data
}

export async function resendWorkEmailVerification(): Promise<BackendSuccessResponse> {
  const response = await api.post('/email/resend-email-verification')
  return response.data
}
